"use client"

import {
  Popover,
  PopoverButton,
  PopoverPanel,
  Transition,
} from "@headlessui/react"
import { convertToLocale } from "@lib/util/money"
import { HttpTypes } from "@medusajs/types"
import { Button } from "@modules/common/components/ui"
import DeleteButton from "@modules/common/components/delete-button"
import LineItemOptions from "@modules/common/components/line-item-options"
import LineItemPrice from "@modules/common/components/line-item-price"
import LocalizedClientLink from "@modules/common/components/localized-client-link"
import Thumbnail from "@modules/products/components/thumbnail"
import { usePathname } from "next/navigation"
import { Fragment, useEffect, useRef, useState } from "react"
import { useT } from "@lib/i18n/use-t"

const CartDropdown = ({
  cart: cartState,
}: {
  cart?: HttpTypes.StoreCart | null
}) => {
  const t = useT()
  const [activeTimer, setActiveTimer] = useState<NodeJS.Timer | undefined>(
    undefined
  )
  const [cartDropdownOpen, setCartDropdownOpen] = useState(false)

  const open = () => setCartDropdownOpen(true)
  const close = () => setCartDropdownOpen(false)

  const totalItems =
    cartState?.items?.reduce((acc, item) => {
      return acc + item.quantity
    }, 0) || 0

  const subtotal = cartState?.subtotal ?? 0
  const itemRef = useRef<number>(totalItems || 0)

  const timedOpen = () => {
    open()

    const timer = setTimeout(close, 5000)

    setActiveTimer(timer)
  }

  const openAndCancel = () => {
    if (activeTimer) {
      clearTimeout(activeTimer)
    }

    open()
  }

  // Clean up the timer when the component unmounts
  useEffect(() => {
    return () => {
      if (activeTimer) {
        clearTimeout(activeTimer)
      }
    }
  }, [activeTimer])

  const pathname = usePathname()

  // open cart dropdown when modifying the cart items, but only if we're not on the cart page
  useEffect(() => {
    if (itemRef.current !== totalItems && !pathname.includes("/cart")) {
      timedOpen()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [totalItems, itemRef.current])

  return (
    <div
      className="h-full z-[100]"
      onMouseEnter={openAndCancel}
      onMouseLeave={close}
    >
      <Popover className="relative h-full">
        <PopoverButton className="h-full">
          <LocalizedClientLink
            className="hover:text-ui-fg-base"
            href="/cart"
            data-testid="nav-cart-link"
          >{`${t("cart")} (${totalItems})`}</LocalizedClientLink>
        </PopoverButton>
        <Transition
          show={cartDropdownOpen}
          as={Fragment}
          enter="transition ease-out duration-200"
          enterFrom="opacity-0 translate-y-1"
          enterTo="opacity-100 translate-y-0"
          leave="transition ease-in duration-150"
          leaveFrom="opacity-100 translate-y-0"
          leaveTo="opacity-0 translate-y-1"
        >
          <PopoverPanel
            static
            className="hidden small:block absolute top-[calc(100%+8px)] right-0 w-[420px] rounded-2xl border border-slate-200/60 bg-white text-slate-900 shadow-2xl dark:border-white/10 dark:bg-[#0f172a] dark:text-[#e8f0ff]"
            data-testid="nav-cart-dropdown"
          >
            <div className="flex items-center justify-between px-6 pt-6 pb-4">
              <h3 className="text-base font-bold text-slate-900 dark:text-[#e8f0ff]">
                {t("cart")}
              </h3>
              <span className="text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                {totalItems}
              </span>
            </div>
            {cartState && cartState.items?.length ? (
              <>
                <ul className="no-scrollbar max-h-[402px] divide-y divide-slate-200/60 overflow-y-auto px-6 dark:divide-white/10">
                  {cartState.items
                    .sort((a, b) => {
                      return (a.created_at ?? "") > (b.created_at ?? "")
                        ? -1
                        : 1
                    })
                    .map((item) => (
                      <li
                        className="grid grid-cols-[64px_1fr] gap-x-3 py-4"
                        key={item.id}
                        data-testid="cart-item"
                      >
                        <LocalizedClientLink
                          href={`/products/${item.product_handle}`}
                          className="block h-16 w-16 overflow-hidden rounded-xl border border-slate-200 bg-slate-50 dark:border-white/10 dark:bg-white/5"
                        >
                          <Thumbnail
                            thumbnail={item.thumbnail}
                            images={item.variant?.product?.images}
                            size="square"
                          />
                        </LocalizedClientLink>
                        <div className="flex flex-1 flex-col">
                          <div className="flex items-start justify-between gap-x-3">
                            <div className="flex min-w-0 flex-1 flex-col">
                              <h3 className="truncate text-sm font-semibold text-slate-900 dark:text-[#e8f0ff]">
                                <LocalizedClientLink
                                  href={`/products/${item.product_handle}`}
                                  data-testid="product-link"
                                  className="transition hover:text-brand-light dark:hover:text-brand-dark"
                                >
                                  {item.title}
                                </LocalizedClientLink>
                              </h3>
                              <div className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
                                <LineItemOptions
                                  variant={item.variant}
                                  data-testid="cart-item-variant"
                                  data-value={item.variant}
                                />
                              </div>
                            </div>
                            <div className="shrink-0 text-sm font-semibold text-slate-900 dark:text-white">
                              <LineItemPrice
                                item={item}
                                style="tight"
                                currencyCode={cartState.currency_code}
                              />
                            </div>
                          </div>
                          <div className="mt-2 flex items-center justify-between">
                            <span
                              className="text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400"
                              data-testid="cart-item-quantity"
                              data-value={item.quantity}
                            >
                              {t("quantity")}: {item.quantity}
                            </span>
                            <DeleteButton
                              id={item.id}
                              className="text-xs text-slate-500 transition hover:text-brand-light dark:text-slate-400 dark:hover:text-brand-dark"
                              data-testid="cart-item-remove-button"
                            >
                              {t("remove")}
                            </DeleteButton>
                          </div>
                        </div>
                      </li>
                    ))}
                </ul>
                <div className="border-t border-slate-200/60 px-6 py-4 dark:border-white/10">
                  <div className="mb-4 flex items-center justify-between">
                    <span className="flex flex-col">
                      <span className="text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                        {t("subtotal")}
                      </span>
                      <span className="text-[11px] text-slate-400 dark:text-slate-500">
                        {t("excludingTaxes")}
                      </span>
                    </span>
                    <span
                      className="text-base font-semibold text-brand-light dark:text-brand-dark"
                      data-testid="cart-subtotal"
                      data-value={subtotal}
                    >
                      {convertToLocale({
                        amount: subtotal,
                        currency_code: cartState.currency_code,
                      })}
                    </span>
                  </div>
                  <LocalizedClientLink href="/cart" passHref>
                    <Button
                      className="w-full rounded-xl bg-brand-light text-white shadow-sm transition hover:bg-brand-light hover:opacity-90 dark:bg-brand-dark dark:text-slate-950 dark:hover:bg-brand-dark"
                      size="large"
                      data-testid="go-to-cart-button"
                    >
                      {t("goToCart")}
                    </Button>
                  </LocalizedClientLink>
                </div>
              </>
            ) : (
              <div className="flex flex-col items-center justify-center gap-y-4 px-6 py-12 text-center">
                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-slate-100 text-slate-500 dark:bg-white/10 dark:text-slate-300">
                  <span className="text-sm font-semibold">0</span>
                </div>
                <p className="text-sm text-slate-600 dark:text-slate-300">
                  {t("cartEmpty")}
                </p>
                <LocalizedClientLink href="/store">
                  <span className="sr-only">{t("goToCatalog")}</span>
                  <Button
                    onClick={close}
                    className="rounded-xl bg-brand-light text-white shadow-sm transition hover:bg-brand-light hover:opacity-90 dark:bg-brand-dark dark:text-slate-950 dark:hover:bg-brand-dark"
                  >
                    {t("goToCatalog")}
                  </Button>
                </LocalizedClientLink>
              </div>
            )}
          </PopoverPanel>
        </Transition>
      </Popover>
    </div>
  )
}

export default CartDropdown
